import React, { useState, useRef } from 'react';
import { PaperClipIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import MentionAutocomplete from './MentionAutocomplete';

const CommentComposer = ({
  initialContent = '',
  initialAttachments = [],
  initialIsInternal = false,
  onSubmit,
  onCancel,
  placeholder = 'Write a comment...',
  submitLabel = 'Comment'
}) => {
  const [content, setContent] = useState(initialContent);
  const [attachments, setAttachments] = useState(initialAttachments);
  const [isInternal, setIsInternal] = useState(initialIsInternal);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [showAttachmentInput, setShowAttachmentInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mention autocomplete state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const textareaRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        content: content.trim(),
        attachments,
        is_internal: isInternal
      });

      // Reset form
      setContent('');
      setAttachments([]);
      setIsInternal(false);
      setAttachmentUrl('');
      setShowAttachmentInput(false);
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAttachment = () => {
    if (attachmentUrl.trim()) {
      const newAttachment = {
        url: attachmentUrl.trim(),
        filename: attachmentUrl.split('/').pop() || 'Link'
      };
      setAttachments([...attachments, newAttachment]);
      setAttachmentUrl('');
      setShowAttachmentInput(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Handle content change and detect @mentions
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    const cursorPos = e.target.selectionStart;

    setContent(newContent);

    // Check if we're in the middle of typing a mention
    // Look backwards from cursor for "@" symbol
    const textBeforeCursor = newContent.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if there's a space between @ and cursor (mentions don't have spaces)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      if (!/\s/.test(textAfterAt)) {
        // No space found - we're in a mention
        setMentionQuery(textAfterAt);
        setMentionStartPos(lastAtIndex);
        setShowMentionAutocomplete(true);

        // Calculate autocomplete position relative to textarea
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          setAutocompletePosition({
            top: rect.height + 5,  // Below textarea
            left: 0
          });
        }
      } else {
        // Space found - not a mention anymore
        setShowMentionAutocomplete(false);
      }
    } else {
      // No @ found
      setShowMentionAutocomplete(false);
    }
  };

  // Handle mention selection from autocomplete
  const handleMentionSelect = (result) => {
    const beforeMention = content.substring(0, mentionStartPos);
    const afterMention = content.substring(textareaRef.current.selectionStart);
    const newContent = `${beforeMention}@${result.mention} ${afterMention}`;

    setContent(newContent);
    setShowMentionAutocomplete(false);
    setMentionQuery('');

    // Focus back on textarea and position cursor after the inserted mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartPos + result.mention.length + 2; // +2 for @ and space
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Close autocomplete
  const handleCloseAutocomplete = () => {
    setShowMentionAutocomplete(false);
    setMentionQuery('');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Textarea with Mention Autocomplete */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          placeholder={placeholder}
          rows={3}
          className="w-full border border-secondary-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          disabled={isSubmitting}
        />

        {/* Mention Autocomplete Dropdown */}
        {showMentionAutocomplete && (
          <MentionAutocomplete
            query={mentionQuery}
            onSelect={handleMentionSelect}
            onClose={handleCloseAutocomplete}
            position={autocompletePosition}
          />
        )}
      </div>

      {/* Attachments Display */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between bg-secondary-50 px-3 py-2 rounded-md"
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <PaperClipIcon className="h-4 w-4 text-secondary-400 flex-shrink-0" />
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 truncate"
                >
                  {attachment.filename}
                </a>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveAttachment(idx)}
                className="text-secondary-400 hover:text-red-600 ml-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attachment Input */}
      {showAttachmentInput && (
        <div className="flex space-x-2">
          <input
            type="url"
            value={attachmentUrl}
            onChange={(e) => setAttachmentUrl(e.target.value)}
            placeholder="https://example.com/file.pdf"
            className="flex-1 border border-secondary-300 rounded-md shadow-sm py-1.5 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          <button
            type="button"
            onClick={handleAddAttachment}
            className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAttachmentInput(false);
              setAttachmentUrl('');
            }}
            className="px-3 py-1.5 bg-secondary-100 text-secondary-700 text-sm rounded-md hover:bg-secondary-200"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Attachment Button */}
          {!showAttachmentInput && (
            <button
              type="button"
              onClick={() => setShowAttachmentInput(true)}
              className="text-secondary-500 hover:text-secondary-700"
              title="Add attachment"
            >
              <PaperClipIcon className="h-5 w-5" />
            </button>
          )}

          {/* Internal Comment Toggle */}
          <button
            type="button"
            onClick={() => setIsInternal(!isInternal)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${
              isInternal
                ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                : 'text-secondary-500 hover:bg-secondary-50'
            }`}
            title="Toggle internal comment"
          >
            <LockClosedIcon className="h-4 w-4" />
            <span>Internal</span>
          </button>
        </div>

        {/* Submit and Cancel */}
        <div className="flex items-center space-x-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!content.trim() || isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CommentComposer;
